/**
 * モデルコース詳細の初期化
 */

/* global google, coursesMapData */

/**
 * Google Mapsルート検索を開く
 *
 * @param {number} lat 緯度
 * @param {number} lng 経度
 */
const routeTo = (lat, lng) => {
  const params = new URLSearchParams({
    api: '1',
    origin: 'Current+Location',
    destination: `${lat},${lng}`,
    travelmode: 'walking'
  });

  const url = `https://www.google.com/maps/dir/?${params.toString()}`;
  window.open(url, '_blank');
};

/**
 * HTMLエスケープ
 *
 * @param {string} str エスケープする文字列
 * @return {string} エスケープ済み文字列
 */
const escapeHtml = (str) => {
  const escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#39;'
  };

  return String(str).replace(/[&<>"']/g, match => escapeMap[match]);
};

/**
 * 属性値エスケープ
 *
 * @param {string} str エスケープする文字列
 * @return {string} エスケープ済み文字列
 */
const escapeAttr = (str) => {
  return String(str).replace(/["']/g, match =>
    match === '"' ? '&quot;' : '&#39;'
  );
};

/**
 * マーカーアイコンを作成
 *
 * @param {string|number} label マーカーラベル（START/GOAL/番号）
 * @param {Object} config 設定オブジェクト
 * @return {Object} Google Maps Iconオブジェクト
 */
const createMarkerIcon = (label, config) => {
  // START/GOALアイコン
  if (label === 'START') {
    return {
      url: `${config.templateUri}/images/icon-start.svg`,
      scaledSize: new google.maps.Size(config.iconSize, config.iconSize),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(config.iconSize / 2, config.iconSize / 2)
    };
  }

  if (label === 'GOAL') {
    return {
      url: `${config.templateUri}/images/icon-goal.svg`,
      scaledSize: new google.maps.Size(config.iconSize, config.iconSize),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(config.iconSize / 2, config.iconSize / 2)
    };
  }

  // 通常のピン（番号付き）
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: config.pinScale,
    fillColor: config.pinColor,
    fillOpacity: 1,
    strokeColor: '#322e2e',
    strokeWeight: 2
  };
};

/**
 * マーカーを作成
 *
 * @param {google.maps.Map} map マップインスタンス
 * @param {Object} location ロケーション情報
 * @param {google.maps.InfoWindow} infoWindow 情報ウィンドウ
 * @param {Object} config 設定オブジェクト
 * @return {google.maps.Marker} マーカーインスタンス
 */
const createMarker = (map, location, infoWindow, config) => {
  const icon = createMarkerIcon(location.label, config);
  const hasCustomIcon = location.label === 'START' || location.label === 'GOAL';

  const marker = new google.maps.Marker({
    position: { lat: location.lat, lng: location.lng },
    map: map,
    title: location.name,
    icon: icon,
    label: hasCustomIcon ? null : {
      text: String(location.label),
      color: '#322e2e',
      fontSize: '14px',
      fontWeight: 'bold'
    }
  });

  // クリックイベント
  marker.addListener('click', () => {
    const infoContent = `
      <div class="s-courses-detail__map--info" style="min-width:200px">
        <div>${escapeHtml(location.name || '')}</div>
        <button onclick="routeTo(${location.lat}, ${location.lng}, '${escapeAttr(location.name || '')}')">
          ${config.routeText}
        </button>
      </div>
    `;

    infoWindow.setContent(infoContent);
    infoWindow.open(map, marker);
  });

  return marker;
};

/**
 * Google Maps初期化
 */
const initCoursesMap = () => {
  // wp_localize_scriptから渡されたデータを取得
  if (typeof coursesMapData === 'undefined') {
    console.error('coursesMapData is not defined. Make sure wp_localize_script is working.');
    return;
  }

  const { mapPins, mapInitList, config } = coursesMapData;

  document.querySelectorAll('.s-courses-detail__map').forEach((mapElement, mapIndex) => {
    const locations = mapPins[mapIndex] || [];
    const initConfig = mapInitList[mapIndex];

    if (!initConfig) {
      console.warn(`Map init config not found for index ${mapIndex}`);
      return;
    }

    // マップを作成
    const map = new google.maps.Map(mapElement, {
      center: { lat: initConfig.lat, lng: initConfig.lng },
      zoom: initConfig.zoom
    });

    const infoWindow = new google.maps.InfoWindow();

    // マーカーを追加
    locations.forEach(location => {
      createMarker(map, location, infoWindow, config);
    });
  });
};

/**
 * モデルコース詳細ページの初期化
 *
 * @param {jQuery} $ jQueryオブジェクト
 */
export function initCoursesDetail($) {
  if ($('body.single-courses').length === 0) return;

  // サムネイル数をチェック
  const $subSlide = $('.s-courses-detail__slide--sub');
  const thumbnailCount = $subSlide.find('.c-slide__item').length;

  // サムネイルが1枚の場合はサブスライドを非表示
  if (thumbnailCount <= 1) {
    $subSlide.hide();

    // メインスライドのみ初期化（サブスライドと連動しない）
    $('.s-courses-detail__slide--main').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      focusOnSelect: true,
      centerMode: true,
      centerPadding: '0',
      variableWidth: true,
      infinite: false,
      arrows: true, // スマホ時は表示
      dots: false,
      responsive: [
        {
          breakpoint: 768, // md未満
          settings: {
            arrows: true,
          },
        },
        {
          breakpoint: 9999, // md以上
          settings: {
            arrows: false,
          },
        },
      ],
    });
  } else {
    // サムネイルが複数ある場合は通常の初期化
    // メインスライド
    $('.s-courses-detail__slide--main').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      asNavFor: '#sub-slide',
      focusOnSelect: false,
      centerMode: true,
      centerPadding: '0',
      variableWidth: true,
      infinite: true,
      arrows: true, // スマホ時は表示
      dots: false,
      responsive: [
        {
          breakpoint: 768, // md未満
          settings: {
            arrows: true,
          },
        },
        {
          breakpoint: 9999, // md以上
          settings: {
            arrows: false,
          },
        },
      ],
    });

    // サブスライド
    $subSlide.slick({
      slidesToShow: 5,
      asNavFor: '#main-slide',
      infinite: true,
      arrows: false, // スマホ時は非表示
      dots: false,
      focusOnSelect: true,
      centerMode: true,
      centerPadding: '0',
      responsive: [
        {
          breakpoint: 768, // md未満
          settings: {
            arrows: false,
          },
        },
        {
          breakpoint: 9999, // md以上
          settings: {
            arrows: true,
          },
        },
      ],
    });

    $subSlide.each(function() {
      if ($(this).find('.c-slide__item').length <= 5) {
        $(this)
          .find('.slick-track')
          .addClass('no-scroll-center');
      }
    });
  }

  // ギャラリースライド
  $('.s-courses-detail__detail--gallery').each(function () {
    const $slider   = $(this);
    const itemCount = $slider.find('.c-slide__item').length;
    const showDots = itemCount > 1;

    $slider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: itemCount > 1,
      arrows: true,
      dots: showDots,
      variableWidth: false,
      adaptiveHeight: true,
    });

    // fancyboxの重複を防ぐため、クローン要素からdata-fancybox属性を削除
    $slider.find('.slick-cloned a.fancybox').removeAttr('data-fancybox');
  });

  // Google Maps初期化は callback で呼び出される
}

// グローバルスコープに公開（インラインハンドラーとコールバックから呼び出せるように）
window.routeTo = routeTo;
window.initCoursesMap = initCoursesMap;
